import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { Eye, EyeOff, School, Lock, Mail, AlertCircle, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuthState } from 'react-firebase-hooks/auth';

const inputClass =
  'w-full bg-[#17140f] border border-[#3a3122] rounded-[11px] py-3 pl-10 pr-4 text-sm text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:border-[#e2916d] focus:ring-2 focus:ring-[#332217] transition-colors';
const labelClass = 'block text-xs font-semibold text-[#b9ad9b] mb-1.5';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);

  // Check if user is already authenticated
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/admin/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#17140f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#b9ad9b] text-sm">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (error: any) {
      let errorMessage = 'An error occurred during login';

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        default:
          errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#17140f] text-[#f3ede2] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#3a3122]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-9 h-9 bg-[#e2916d] rounded-[10px] flex items-center justify-center flex-shrink-0">
              <School className="w-[18px] h-[18px] text-[#241408]" />
            </div>
            <span className="text-base font-bold">Kaksha360</span>
          </div>
          <button
            onClick={() => navigate('/register')}
            className="flex items-center gap-1 text-sm font-semibold text-[#b9ad9b] hover:text-[#e2916d] transition-colors"
          >
            New to Kaksha360? Register your school
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="max-w-[400px] w-full">
          <div className="text-center mb-8">
            <div className="mx-auto w-12 h-12 bg-[#e2916d] rounded-2xl flex items-center justify-center mb-4">
              <School className="w-6 h-6 text-[#241408]" />
            </div>
            <h1 className="text-2xl font-bold mb-1.5">Welcome back</h1>
            <p className="text-sm text-[#b9ad9b]">Sign in to your school's admin portal</p>
          </div>

          <div className="bg-[#211c15] border border-[#3a3122] rounded-[22px] p-7">
            {error && (
              <div className="mb-5 p-3 bg-[#341a17] border border-[#f38b83]/30 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#f38b83] flex-shrink-0" />
                <span className="text-[#f38b83] text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className={labelClass}>Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="admin@school.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className={labelClass}>Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputClass} pr-11`}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a7f6e] hover:text-[#b9ad9b] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#e2916d] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-transform text-[#241408] font-semibold text-sm py-3 rounded-xl"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-[#241408] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#3a3122]" />
                </div>
                <span className="relative bg-[#211c15] px-3 text-xs text-[#8a7f6e]">New to Kaksha360?</span>
              </div>
              <button
                onClick={() => navigate('/register')}
                className="mt-4 w-full border border-[#3a3122] hover:border-[#e2916d] hover:text-[#e2916d] transition-colors text-[#f3ede2] font-semibold text-sm py-3 rounded-xl"
              >
                Register Your School
              </button>
            </div>

            <p className="mt-6 text-center text-xs text-[#b9ad9b]">
              Having trouble accessing your account?{' '}
              <a href="mailto:support@kaksha360.com" className="text-[#e2916d] font-semibold hover:underline">Contact support</a>
            </p>
          </div>
        </div>
      </div>

      <div className="text-center pb-8">
        <p className="text-xs text-[#8a7f6e]">© {new Date().getFullYear()} Kaksha360. All rights reserved.</p>
      </div>
    </div>
  );
};

export default Login;
